import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import type { Stage } from "@breeze/shared";
import { STAGES, STAGE_LABELS } from "@breeze/shared";
import { useSelection } from "../store/selection.js";
import { bulkPatchCards } from "../api.js";
import { STAGE_ACCENT } from "../theme.js";
import { useCurrentUser } from "../store/filters.js";

export function BulkBar(): JSX.Element {
  const { selected, clear } = useSelection();
  const currentUser = useCurrentUser();
  const count = selected.size;
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const ids = Array.from(selected);

  const run = async (
    label: string,
    patch: Parameters<typeof bulkPatchCards>[0]["patch"]
  ): Promise<void> => {
    if (ids.length === 0) return;
    setBusy(label);
    setError(null);
    try {
      const res = await bulkPatchCards({ cardIds: ids, patch });
      const failed = res.results.filter((r) => !r.ok);
      if (failed.length > 0) {
        setError(`${failed.length} of ${ids.length} failed`);
      } else {
        clear();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <AnimatePresence>
      {count > 0 ? (
        <motion.div
          className="fixed bottom-4 left-1/2 z-20 hidden -translate-x-1/2 items-center gap-2 rounded-xl border border-ink-600 bg-ink-800/95 px-3 py-2 text-[12px] text-ink-100 shadow-drag backdrop-blur md:flex"
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 26 }}
        >
          <span className="rounded-md bg-slack-aubergine px-2.5 py-1 font-medium text-ink-50">
            {count} selected
          </span>

          <StageDropdown
            disabled={busy !== null}
            onPick={(stage) => {
              if (stage === "lost") {
                setError("Use single-card menu for Lost (reason required)");
                return;
              }
              void run("move", { stage });
            }}
          />

          <button
            disabled={busy !== null}
            onClick={() => void run("priority", { priority: 1 })}
            className="rounded-md border border-ink-600 px-2.5 py-1 uppercase tracking-wider hover:border-slack-yellow hover:text-slack-yellow disabled:opacity-50"
            title="Mark all selected as priority"
          >
            ⚡ Priority
          </button>

          <button
            disabled={busy !== null}
            onClick={() => void run("assign", { assignedUser: currentUser })}
            className="rounded-md border border-ink-600 px-2.5 py-1 uppercase tracking-wider hover:border-slack-aubergineLight hover:text-ink-50 disabled:opacity-50"
          >
            Assign mine
          </button>

          <button
            disabled={busy !== null}
            onClick={() => void run("archive", { archive: true })}
            className="rounded-md border border-ink-500 px-2.5 py-1 uppercase tracking-wider hover:border-slack-aubergineLight hover:text-ink-50 disabled:opacity-50"
          >
            Archive
          </button>

          <button
            disabled={busy !== null}
            onClick={() => clear()}
            className="rounded-md border border-ink-700 px-2 py-1 text-ink-200 hover:border-ink-500 hover:text-ink-100 disabled:opacity-50"
          >
            Clear
          </button>

          {error ? (
            <span className="text-[11px] text-slack-pink">{error}</span>
          ) : null}
          {busy ? (
            <span className="text-[11px] text-slack-yellow">{busy}…</span>
          ) : null}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

function StageDropdown({
  onPick,
  disabled,
}: {
  onPick: (stage: Stage) => void;
  disabled: boolean;
}): JSX.Element {
  return (
    <div className="relative">
      <select
        disabled={disabled}
        onChange={(e) => {
          const v = e.target.value;
          if (v) {
            onPick(v as Stage);
            e.currentTarget.value = "";
          }
        }}
        defaultValue=""
        className="appearance-none rounded-md border border-ink-600 bg-ink-700 px-2.5 py-1 text-[12px] text-ink-100 disabled:opacity-50"
      >
        <option value="" disabled>
          Move to ▾
        </option>
        {STAGES.map((s) => (
          <option key={s} value={s} style={{ color: STAGE_ACCENT[s] }}>
            {STAGE_LABELS[s]}
          </option>
        ))}
      </select>
    </div>
  );
}
